// ---------------------------------------------------------------------------
// Per-document renderer startup recorder (Spec 4 / D1).
//
// WHY a second metric instead of extending boot_duration_ms: the canonical
// boot_* lifecycle events are claimed ONCE PER ELECTRON LAUNCH in sessionStorage
// (routes/root/bootstrap-analytics.ts + analytics/domains/boot-lifecycle.ts), so
// N renderer refreshes under one launch produce ONE canonical sample, and a
// refresh that lands on /room/:id never mounts the root bootstrap hook at all.
// Clearing those claims to get more samples would break the one-boot-one-
// terminal contract those guards exist to enforce. This recorder therefore
// measures a DIFFERENT thing — one document's time from navigation start to
// first usable render — under its own `renderer_load_id`, and leaves every
// canonical boot event, claim and watchdog untouched.
//
// WHY it imports nothing from the app: auth/depot/logger/PostHog are all on the
// eager boot graph this instrumentation is meant to measure. Importing any of
// them from here would add a dependency cycle into the very path under test and
// could change what it measures. The analytics bridge lives in the separate
// renderer-startup-analytics.tsx component, which depends on this module and
// not the other way round.
//
// ACCOUNTING CONTRACT (review-4 §5 step 2): the `document` lane is a DISJOINT
// partition — its spans tile [0, end] with no overlap, so they may be summed.
// Every other lane (root bootstrap attempts) and every operation span may
// overlap anything, including each other; they are reported separately and MUST
// NOT be added to elapsed startup time. `span_kind` carries that distinction to
// the consumer.
//
// Per rules: think-in-systems, code-comments-required, reference-rules-in-comments
// (ai.knowledge/global).
// ---------------------------------------------------------------------------

/** The one disjoint lane whose spans tile the document's lifetime. */
export const RENDERER_DOCUMENT_LANE = 'document';

/**
 * Ordered, forward-only phases of the disjoint document partition.
 *
 * `pre-entry` is the interval before this module was first touched. It is
 * deliberately NOT called "bundle CPU": it contains HTML parse, CSS/JS transfer,
 * evaluation of the eager module graph AND any network/idle time in between.
 * Splitting it needs the external trace (review-4 §5 step 7), not this recorder.
 */
export const RENDERER_DOCUMENT_PHASES = [
  'pre-entry',
  'until-shell',
  'until-overlay-ready',
  'until-usable'
] as const;

export type RendererDocumentPhase = (typeof RENDERER_DOCUMENT_PHASES)[number];

/**
 * `phase` spans belong to a lane and are exclusive WITHIN that lane.
 * `operation` spans are free-floating and may overlap anything.
 * `milestone` rows are points in time (duration 0), not intervals.
 */
export type RendererSpanKind = 'phase' | 'operation' | 'milestone';

export type RendererSpanOutcome = 'ok' | 'error' | 'cancelled' | 'document-replaced';

export type RendererDocumentOutcome = 'usable' | 'error' | 'document-replaced';

export type RendererEndpoint = 'observed-controls' | 'overlay-ready';

export type RendererDestination =
  | 'room'
  | 'offline'
  | 'empty-room'
  | 'login'
  | 'wizard'
  | 'unknown';

/** Coarse route shape. Never carries a room/org id or a raw query string. */
export type RendererRouteKind =
  | 'index'
  | 'room'
  | 'room-boot'
  | 'setup'
  | 'prequal'
  | 'auth'
  | 'other';

export type RendererSpanRow = {
  readonly kind: 'span';
  readonly span_kind: RendererSpanKind;
  readonly renderer_load_id: string;
  readonly lane: string;
  readonly phase: string;
  readonly sequence: number;
  readonly start_ms: number;
  readonly end_ms: number;
  readonly duration_ms: number;
  readonly outcome: RendererSpanOutcome;
};

export type RendererDocumentRow = {
  readonly kind: 'document';
  readonly renderer_load_id: string;
  readonly renderer_startup_duration_ms: number;
  readonly outcome: RendererDocumentOutcome;
  readonly endpoint: RendererEndpoint;
  readonly destination: RendererDestination;
  readonly initial_route_kind: RendererRouteKind;
  readonly navigation_type: string;
  readonly span_count: number;
  readonly dropped_span_count: number;
  // Navigation Timing. `null` means "not available / not yet settled" — a zero
  // from the API is reported as null on purpose, because zero there means the
  // event had not fired, NOT that the work was free (review-4 §4).
  readonly nav_response_end_ms: number | null;
  readonly nav_dom_content_loaded_ms: number | null;
  readonly nav_load_event_end_ms: number | null;
  // Resource Timing for scripts that finished before the endpoint. Named
  // `loaded_js_*`, never "evaluated": Resource Timing observes LOADS, and
  // execution cost is only obtainable from the external trace (review-4 §5.5).
  readonly loaded_js_count: number | null;
  readonly loaded_js_transfer_bytes: number | null;
  readonly loaded_js_encoded_bytes: number | null;
  readonly loaded_js_decoded_bytes: number | null;
  readonly loaded_js_sizes_available: boolean;
  // Main-thread pressure. Long tasks are only observable where the entry type is
  // supported; `null` distinguishes "unsupported" from "none observed" (0).
  readonly long_task_count: number | null;
  readonly long_task_total_ms: number | null;
  readonly long_task_max_ms: number | null;
  // Timer lateness sampler: actual-minus-scheduled for a foreground-only
  // interval. Per knowledge late-timer-means-blocked-main-thread, a constant
  // overshoot is evidence of scheduling delay — NOT by itself proof of
  // synchronous CPU, which is why hidden time is reported alongside it.
  readonly timer_sample_count: number;
  readonly timer_lateness_max_ms: number | null;
  readonly timer_lateness_p95_ms: number | null;
  readonly hidden_ms: number;
};

export type RendererDiagnosticRow = RendererSpanRow | RendererDocumentRow;

export type RendererDiagnosticsSink = (row: RendererDiagnosticRow) => void;

export type NavigationTimingSnapshot = {
  readonly responseEnd: number;
  readonly domContentLoadedEventEnd: number;
  readonly loadEventEnd: number;
  readonly type: string;
};

export type ScriptResourceSnapshot = {
  readonly count: number;
  readonly transferBytes: number;
  readonly encodedBytes: number;
  readonly decodedBytes: number;
  /** False when every observed script reported zero size (cached / cross-origin). */
  readonly sizesAvailable: boolean;
};

/**
 * Every environment touch the recorder makes. Injected so unit tests drive a
 * fake clock and no wall-clock sleeps are needed (review-4 §5 step 2).
 */
export type RendererStartupEnvironment = {
  readonly now: () => number;
  readonly randomUUID: () => string;
  /** `startTimeMs` places the mark in the past; omitted means "now". */
  readonly mark: (name: string, startTimeMs?: number) => void;
  readonly measure: (name: string, startMark: string, endMark: string) => void;
  readonly initialPath: string;
  readonly navigationType: string;
  /** Starts a repeating timer; returns its canceller. */
  readonly scheduleInterval: (callback: () => void, intervalMs: number) => () => void;
  readonly isHidden: () => boolean;
  /** Subscribes to long tasks; returns an unsubscribe. `undefined` when unsupported. */
  readonly observeLongTasks: ((onTask: (durationMs: number) => void) => () => void) | undefined;
  readonly readNavigationTiming: () => NavigationTimingSnapshot | undefined;
  readonly readScriptResources: (beforeMs: number) => ScriptResourceSnapshot | undefined;
  /** Subscribes to document teardown (pagehide); returns an unsubscribe. */
  readonly onDocumentHide: ((onHide: () => void) => () => void) | undefined;
};

// Bounded so a wedged boot cannot grow the buffer without limit while no sink
// is attached. Overflow is counted, not silently lost.
const MAX_BUFFERED_ROWS = 512;
const TIMER_SAMPLE_INTERVAL_MS = 100;

const routeKindFor = (path: string): RendererRouteKind => {
  if (path === '' || path === '/') return 'index';
  if (/^\/room\/[^/]+\/boot/.test(path)) return 'room-boot';
  if (path.startsWith('/room/')) return 'room';
  if (path.includes('prequal')) return 'prequal';
  if (path.includes('/setup')) return 'setup';
  if (path.includes('login') || path.includes('auth')) return 'auth';
  return 'other';
};

// A performance entry value of 0 means "this event has not happened yet" for
// navigation timings. Reporting it as 0 would read as "free"; report null.
const nullIfUnset = (value: number | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

const percentile = (sorted: readonly number[], fraction: number): number | null => {
  if (sorted.length === 0) return null;
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(fraction * sorted.length) - 1));
  return sorted[index] ?? null;
};

const defaultRandomUUID = (): string => {
  try {
    const cryptoRef = globalThis.crypto as { randomUUID?: () => string } | undefined;
    if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
      return cryptoRef.randomUUID();
    }
  } catch {
    // Fall through to the arithmetic id below.
  }
  return `rl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const supportsEntryType = (type: string): boolean => {
  try {
    const supported = PerformanceObserver.supportedEntryTypes;
    return Array.isArray(supported) && supported.includes(type);
  } catch {
    return false;
  }
};

/** The subset of PerformanceResourceTiming this module reads. */
export type ScriptResourceEntry = {
  readonly initiatorType: string;
  readonly responseEnd: number;
  readonly transferSize: number;
  readonly encodedBodySize: number;
  readonly decodedBodySize: number;
};

/**
 * Aggregates the script resources that finished loading before `beforeMs`.
 *
 * `sizesAvailable` means the BODY sizes are usable as weight. transferSize alone
 * does NOT qualify: a locally served or cached response reports a small non-zero
 * transferSize with encoded/decoded body sizes of 0, and publishing that as
 * "available" makes a 0-byte bundle weight read as "the bundle is free" instead
 * of "this run could not measure it". A cross-origin response without
 * Timing-Allow-Origin reports 0 for all three and is equally unusable.
 *
 * Exported (and pure) so the rule is testable without a browser.
 */
export const summarizeScriptResources = (
  entries: readonly ScriptResourceEntry[],
  beforeMs: number
): ScriptResourceSnapshot => {
  let count = 0;
  let transferBytes = 0;
  let encodedBytes = 0;
  let decodedBytes = 0;
  let sizesAvailable = false;

  for (const entry of entries) {
    if (entry.initiatorType !== 'script' || entry.responseEnd > beforeMs) continue;
    count += 1;
    transferBytes += entry.transferSize;
    encodedBytes += entry.encodedBodySize;
    decodedBytes += entry.decodedBodySize;
    if (entry.encodedBodySize > 0 || entry.decodedBodySize > 0) sizesAvailable = true;
  }

  return { count, transferBytes, encodedBytes, decodedBytes, sizesAvailable };
};

/**
 * Browser-backed environment. Every capability is feature-detected: a context
 * without PerformanceObserver, without a specific entry type, or without a
 * document still records spans — it just reports `null` for what it cannot see.
 */
export const createBrowserEnvironment = (): RendererStartupEnvironment => {
  const hasPerformance = typeof performance !== 'undefined';
  const hasWindow = typeof window !== 'undefined';
  const hasDocument = typeof document !== 'undefined';

  const navigationEntry = (): PerformanceNavigationTiming | undefined => {
    if (!hasPerformance || typeof performance.getEntriesByType !== 'function') return undefined;
    try {
      return performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
    } catch {
      return undefined;
    }
  };

  return {
    now: () => (hasPerformance ? performance.now() : Date.now()),
    randomUUID: defaultRandomUUID,
    mark: (name, startTimeMs) => {
      if (!hasPerformance || typeof performance.mark !== 'function') return;
      try {
        // A span opened retroactively (pre-entry at 0) needs its mark in the
        // past too, or the trace measures ~0 ms while the row says otherwise.
        performance.mark(name, startTimeMs === undefined ? undefined : { startTime: startTimeMs });
      } catch {
        // Instrumentation must never throw into boot.
      }
    },
    measure: (name, startMark, endMark) => {
      if (!hasPerformance || typeof performance.measure !== 'function') return;
      try {
        performance.measure(name, startMark, endMark);
      } catch {
        // A missing mark (buffer eviction) is not an error worth surfacing.
      }
    },
    initialPath: hasWindow ? window.location.pathname : '',
    navigationType: navigationEntry()?.type ?? 'unknown',
    scheduleInterval: (callback, intervalMs) => {
      const handle = setInterval(callback, intervalMs);
      return () => clearInterval(handle);
    },
    isHidden: () => (hasDocument ? document.visibilityState === 'hidden' : false),
    observeLongTasks:
      typeof PerformanceObserver !== 'undefined' && supportsEntryType('longtask')
        ? (onTask) => {
            try {
              const observer = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) onTask(entry.duration);
              });
              // `buffered` replays long tasks that happened before this
              // subscription — the pre-entry interval is exactly where they hurt.
              observer.observe({ type: 'longtask', buffered: true });
              return () => observer.disconnect();
            } catch {
              return () => undefined;
            }
          }
        : undefined,
    readNavigationTiming: () => {
      const entry = navigationEntry();
      if (!entry) return undefined;
      return {
        responseEnd: entry.responseEnd,
        domContentLoadedEventEnd: entry.domContentLoadedEventEnd,
        loadEventEnd: entry.loadEventEnd,
        type: entry.type
      };
    },
    readScriptResources: (beforeMs) => {
      if (!hasPerformance || typeof performance.getEntriesByType !== 'function') return undefined;
      try {
        return summarizeScriptResources(
          performance.getEntriesByType('resource') as PerformanceResourceTiming[],
          beforeMs
        );
      } catch {
        return undefined;
      }
    },
    onDocumentHide: hasWindow
      ? (onHide) => {
          const handler = (): void => onHide();
          window.addEventListener('pagehide', handler, { once: true });
          return () => window.removeEventListener('pagehide', handler);
        }
      : undefined
  };
};

type OpenSpan = {
  lane: string;
  phase: string;
  sequence: number;
  startMs: number;
  markName: string;
};

export type RendererStartupRecorder = {
  startDocument: () => void;
  markMilestone: (name: string) => void;
  beginOperation: (name: string, lane: string) => (outcome: RendererSpanOutcome) => void;
  enterPhase: (lane: string, phase: string) => void;
  advanceDocumentPhase: (phase: RendererDocumentPhase) => void;
  finishLane: (lane: string, outcome: RendererSpanOutcome) => void;
  finishDocument: (input: {
    outcome: RendererDocumentOutcome;
    endMs: number;
    endpoint: RendererEndpoint;
    destination: RendererDestination;
  }) => void;
  setSink: (sink: RendererDiagnosticsSink | undefined) => void;
  readRows: () => readonly RendererDiagnosticRow[];
  getLoadId: () => string;
  isFinished: () => boolean;
};

/**
 * Builds one recorder bound to one document. Exported so tests can construct an
 * isolated instance with a fake clock instead of mutating module state.
 */
export const createRendererStartupRecorder = (
  environment: RendererStartupEnvironment
): RendererStartupRecorder => {
  const env = environment;
  const loadId = env.randomUUID();
  const routeKind = routeKindFor(env.initialPath);

  let started = false;
  let finished = false;
  let rows: RendererDiagnosticRow[] = [];
  let droppedRows = 0;
  let flushedCount = 0;
  let sink: RendererDiagnosticsSink | undefined;
  let sequenceCounter = 0;

  const laneSpans = new Map<string, OpenSpan>();
  const openOperations = new Set<OpenSpan>();
  const seenMilestones = new Set<string>();

  let documentPhaseIndex = -1;
  let lastBoundaryMs = 0;

  let longTaskCount: number | null = null;
  let longTaskTotalMs = 0;
  let longTaskMaxMs = 0;
  let stopLongTasks: (() => void) | undefined;

  let timerLateness: number[] = [];
  let hiddenMs = 0;
  let stopSampler: (() => void) | undefined;
  let stopHideListener: (() => void) | undefined;

  let bootstrapEntryScripts: ScriptResourceSnapshot | undefined;

  const safeNow = (): number => {
    const value = env.now();
    return Number.isFinite(value) ? value : 0;
  };

  const emit = (row: RendererDiagnosticRow): void => {
    if (rows.length >= MAX_BUFFERED_ROWS) {
      droppedRows += 1;
      // Keep the document summary reachable even when the buffer is full: drop
      // the oldest span rather than the newest row.
      if (row.kind === 'document') {
        rows.shift();
        if (flushedCount > 0) flushedCount -= 1;
      } else {
        return;
      }
    }
    rows.push(row);
    if (!sink) return;
    // Only rows not yet handed to a sink are sent, so a StrictMode
    // detach/attach cycle cannot replay what already went out.
    try {
      sink(row);
      flushedCount = rows.length;
    } catch {
      // A throwing consumer must not break boot; the row stays buffered.
    }
  };

  const markName = (lane: string, phase: string, sequence: number, edge: 'start' | 'end'): string =>
    `iris.renderer-startup.${loadId}.${lane}.${phase}.${sequence}.${edge}`;

  const openSpan = (lane: string, phase: string, startMs: number): OpenSpan => {
    sequenceCounter += 1;
    const span: OpenSpan = {
      lane,
      phase,
      sequence: sequenceCounter,
      startMs,
      markName: markName(lane, phase, sequenceCounter, 'start')
    };
    env.mark(span.markName, startMs);
    return span;
  };

  const closeSpan = (
    span: OpenSpan,
    endMs: number,
    outcome: RendererSpanOutcome,
    spanKind: RendererSpanKind
  ): void => {
    const endMarkName = markName(span.lane, span.phase, span.sequence, 'end');
    env.mark(endMarkName);
    env.measure(
      `iris.renderer-startup.${loadId}.${span.lane}.${span.phase}.${span.sequence}`,
      span.markName,
      endMarkName
    );
    emit({
      kind: 'span',
      span_kind: spanKind,
      renderer_load_id: loadId,
      lane: span.lane,
      phase: span.phase,
      sequence: span.sequence,
      start_ms: span.startMs,
      end_ms: endMs,
      duration_ms: Math.max(0, endMs - span.startMs),
      outcome
    });
  };

  const startSamplers = (): void => {
    if (env.observeLongTasks) {
      longTaskCount = 0;
      stopLongTasks = env.observeLongTasks((durationMs) => {
        longTaskCount = (longTaskCount ?? 0) + 1;
        longTaskTotalMs += durationMs;
        longTaskMaxMs = Math.max(longTaskMaxMs, durationMs);
      });
    }

    let expectedAt = safeNow() + TIMER_SAMPLE_INTERVAL_MS;
    let lastTickMs = safeNow();
    stopSampler = env.scheduleInterval(() => {
      const actual = safeNow();
      if (env.isHidden()) {
        // Background throttling makes lateness meaningless and stretches the
        // tick to ≥1 s, so count the real gap since the last tick — the nominal
        // interval would under-report hidden time ~10×.
        hiddenMs += Math.max(0, actual - lastTickMs);
      } else {
        timerLateness.push(Math.max(0, actual - expectedAt));
      }
      lastTickMs = actual;
      expectedAt = actual + TIMER_SAMPLE_INTERVAL_MS;
    }, TIMER_SAMPLE_INTERVAL_MS);
  };

  const stopSamplers = (): void => {
    stopLongTasks?.();
    stopLongTasks = undefined;
    stopSampler?.();
    stopSampler = undefined;
    stopHideListener?.();
    stopHideListener = undefined;
  };

  const closeEverythingOpen = (endMs: number, outcome: RendererSpanOutcome): void => {
    for (const span of openOperations) {
      closeSpan(span, endMs, outcome, 'operation');
    }
    openOperations.clear();
    for (const [lane, span] of laneSpans) {
      if (lane === RENDERER_DOCUMENT_LANE) continue;
      closeSpan(span, endMs, outcome, 'phase');
      laneSpans.delete(lane);
    }
  };

  const enterPhase = (lane: string, phase: string): void => {
    if (finished) return;
    const now = safeNow();
    const previous = laneSpans.get(lane);
    if (previous) {
      // Same timestamp on both edges: the lane is exclusive, so its spans tile
      // without gaps or overlap.
      closeSpan(previous, now, 'ok', 'phase');
    }
    laneSpans.set(lane, openSpan(lane, phase, now));
    if (lane === RENDERER_DOCUMENT_LANE) lastBoundaryMs = now;
  };

  const startDocument = (): void => {
    if (started) return;
    started = true;
    const now = safeNow();
    // Everything before this call belongs to one honestly-named bucket.
    const preEntry = openSpan(RENDERER_DOCUMENT_LANE, 'pre-entry', 0);
    closeSpan(preEntry, now, 'ok', 'phase');
    lastBoundaryMs = now;
    documentPhaseIndex = 1;
    laneSpans.set(RENDERER_DOCUMENT_LANE, openSpan(RENDERER_DOCUMENT_LANE, 'until-shell', now));
    startSamplers();
    if (env.onDocumentHide) {
      stopHideListener = env.onDocumentHide(() => {
        // A replaced document is never a success, however far boot got.
        finishDocument({
          outcome: 'document-replaced',
          endMs: safeNow(),
          endpoint: 'overlay-ready',
          destination: 'unknown'
        });
      });
    }
  };

  const advanceDocumentPhase = (phase: RendererDocumentPhase): void => {
    if (!started || finished) return;
    const nextIndex = RENDERER_DOCUMENT_PHASES.indexOf(phase);
    // Forward-only: a late or duplicate advance (StrictMode remount, overlay
    // re-entry after the dismissal latch) must not reopen an earlier phase and
    // break the partition's ordering.
    if (nextIndex <= documentPhaseIndex) return;
    documentPhaseIndex = nextIndex;
    enterPhase(RENDERER_DOCUMENT_LANE, phase);
  };

  const markMilestone = (name: string): void => {
    if (finished) return;
    if (seenMilestones.has(name)) return;
    seenMilestones.add(name);
    const now = safeNow();
    env.mark(`iris.renderer-startup.${loadId}.milestone.${name}`);
    sequenceCounter += 1;
    emit({
      kind: 'span',
      span_kind: 'milestone',
      renderer_load_id: loadId,
      lane: 'milestone',
      phase: name,
      sequence: sequenceCounter,
      start_ms: now,
      end_ms: now,
      duration_ms: 0,
      outcome: 'ok'
    });
    // The first bootstrap entry is the review-mandated snapshot point for
    // "scripts loaded before the boot generator started".
    if (name === 'root-bootstrap-started' && bootstrapEntryScripts === undefined) {
      bootstrapEntryScripts = env.readScriptResources(now);
    }
  };

  const beginOperation = (name: string, lane: string): ((outcome: RendererSpanOutcome) => void) => {
    if (finished) return () => undefined;
    const span = openSpan(lane, name, safeNow());
    openOperations.add(span);
    let closed = false;
    return (outcome: RendererSpanOutcome) => {
      if (closed) return;
      closed = true;
      if (!openOperations.delete(span)) return;
      closeSpan(span, safeNow(), outcome, 'operation');
    };
  };

  const finishLane = (lane: string, outcome: RendererSpanOutcome): void => {
    if (finished) return;
    // The document partition is closed only by finishDocument.
    if (lane === RENDERER_DOCUMENT_LANE) return;
    const span = laneSpans.get(lane);
    if (!span) return;
    laneSpans.delete(lane);
    closeSpan(span, safeNow(), outcome, 'phase');
  };

  function finishDocument(input: {
    outcome: RendererDocumentOutcome;
    endMs: number;
    endpoint: RendererEndpoint;
    destination: RendererDestination;
  }): void {
    if (!started || finished) return;
    // Validate the externally supplied timestamp: an e2e-observed UI time comes
    // from the page, but a NaN or a value behind the last boundary would produce
    // a negative span and a nonsense total.
    const endMs =
      Number.isFinite(input.endMs) && input.endMs >= lastBoundaryMs ? input.endMs : safeNow();
    finished = true;

    // The final partition span carries the document's OWN outcome. A boot that
    // ended on the error screen is a failed sample, not a replaced document:
    // collapsing the two would make error boots indistinguishable from reloads
    // in the phase table and hide the slowest, worst user experience.
    const documentSpanOutcome: RendererSpanOutcome =
      input.outcome === 'usable' ? 'ok' : input.outcome === 'error' ? 'error' : 'document-replaced';
    // Operations and attempt lanes still in flight at the end did not complete.
    // They are 'cancelled' unless the document itself was replaced, which is the
    // only case where "the page went away underneath them" is the true story.
    const leftoverOutcome: RendererSpanOutcome =
      input.outcome === 'document-replaced' ? 'document-replaced' : 'cancelled';

    const documentSpan = laneSpans.get(RENDERER_DOCUMENT_LANE);
    laneSpans.delete(RENDERER_DOCUMENT_LANE);
    closeEverythingOpen(endMs, leftoverOutcome);
    if (documentSpan) {
      closeSpan(documentSpan, endMs, documentSpanOutcome, 'phase');
    }
    stopSamplers();

    const navigation = env.readNavigationTiming();
    const scripts = bootstrapEntryScripts ?? env.readScriptResources(endMs);
    const sortedLateness = [...timerLateness].sort((a, b) => a - b);

    emit({
      kind: 'document',
      renderer_load_id: loadId,
      renderer_startup_duration_ms: Math.max(0, endMs),
      outcome: input.outcome,
      endpoint: input.endpoint,
      destination: input.destination,
      initial_route_kind: routeKind,
      navigation_type: navigation?.type ?? env.navigationType,
      span_count: rows.length,
      dropped_span_count: droppedRows,
      nav_response_end_ms: nullIfUnset(navigation?.responseEnd),
      nav_dom_content_loaded_ms: nullIfUnset(navigation?.domContentLoadedEventEnd),
      nav_load_event_end_ms: nullIfUnset(navigation?.loadEventEnd),
      loaded_js_count: scripts?.count ?? null,
      loaded_js_transfer_bytes: scripts?.transferBytes ?? null,
      loaded_js_encoded_bytes: scripts?.encodedBytes ?? null,
      loaded_js_decoded_bytes: scripts?.decodedBytes ?? null,
      loaded_js_sizes_available: scripts?.sizesAvailable ?? false,
      long_task_count: longTaskCount,
      long_task_total_ms: longTaskCount === null ? null : longTaskTotalMs,
      long_task_max_ms: longTaskCount === null ? null : longTaskMaxMs,
      timer_sample_count: sortedLateness.length,
      timer_lateness_max_ms: sortedLateness.at(-1) ?? null,
      timer_lateness_p95_ms: percentile(sortedLateness, 0.95),
      hidden_ms: hiddenMs
    });
  }

  return {
    startDocument,
    markMilestone,
    beginOperation,
    enterPhase,
    advanceDocumentPhase,
    finishLane,
    finishDocument,
    setSink: (next) => {
      sink = next;
      if (!sink) return;
      // Flush the backlog exactly once per row.
      const pending = rows.slice(flushedCount);
      flushedCount = rows.length;
      for (const row of pending) {
        try {
          sink(row);
        } catch {
          // See emit(): a throwing sink is contained here.
        }
      }
    },
    readRows: () => rows,
    getLoadId: () => loadId,
    isFinished: () => finished
  };
};

/**
 * Coarse destination for a finished document, derived from the URL the renderer
 * ended on. Never carries the id itself.
 */
export const rendererDestinationForPath = (path: string): RendererDestination => {
  if (path.startsWith('/room/')) return 'room';
  if (path.includes('prequal')) return 'wizard';
  if (path.includes('/setup')) return 'wizard';
  if (path.includes('login') || path.includes('oauth')) return 'login';
  return 'unknown';
};

/**
 * True only in an explicit e2e profiling run.
 *
 * The overlay-dismissal effect closes the document at the `overlay-ready` proxy
 * endpoint in normal operation. A profiling run needs the partition left OPEN so
 * the harness can close it at the stricter, externally observed
 * `observed-controls` endpoint (overlay actually faded AND the producer controls
 * actionable). Both conditions are required: the e2e env gate (read off the
 * preload bridge, as every other probe does — `process.env` is not populated in
 * this renderer) and the per-document marker Playwright installs with
 * addInitScript. Production can satisfy neither.
 */
export const isRendererStartupProfiling = (): boolean => {
  try {
    const scope = globalThis as { __irisStartupProfile?: boolean };
    return scope.__irisStartupProfile === true && isRendererE2eRun();
  } catch {
    return false;
  }
};

/**
 * True in an e2e-launched renderer. Read off the preload bridge as every other
 * probe does — `process.env` is not populated in this renderer — so production
 * can never satisfy it. Shared by the profiling gate and the analytics sampler.
 */
export const isRendererE2eRun = (): boolean => {
  try {
    const scope = globalThis as {
      window?: { electron?: { process?: { env?: Record<string, string> } } };
    };
    return scope.window?.electron?.process?.env?.['IRIS_E2E_TEST'] === '1';
  } catch {
    return false;
  }
};

// ── Module singleton ────────────────────────────────────────────────────────
// Lazy: importing this module must cost nothing until the renderer actually
// starts recording, and a non-browser context (jest module graph, SSR) must not
// touch `performance` or `window` at import time.

let singleton: RendererStartupRecorder | undefined;

const recorder = (): RendererStartupRecorder => {
  singleton ??= createRendererStartupRecorder(createBrowserEnvironment());
  return singleton;
};

/** Marks the renderer entry point and opens the disjoint document partition. */
export const startRendererDocument = (): void => recorder().startDocument();

/** Records a point in time. Only the first occurrence of a name is kept. */
export const markRendererMilestone = (name: string): void => recorder().markMilestone(name);

/** Opens an overlapping operation span; the returned closer is idempotent. */
export const beginRendererOperation = (
  name: string,
  lane: string
): ((outcome: RendererSpanOutcome) => void) => recorder().beginOperation(name, lane);

/** Closes the lane's current phase and opens `phase` at the same timestamp. */
export const enterRendererPhase = (lane: string, phase: string): void =>
  recorder().enterPhase(lane, phase);

/** Forward-only advance of the disjoint document partition. */
export const advanceRendererDocumentPhase = (phase: RendererDocumentPhase): void =>
  recorder().advanceDocumentPhase(phase);

/** Closes a lane's open phase with an explicit outcome. */
export const finishRendererLane = (lane: string, outcome: RendererSpanOutcome): void =>
  recorder().finishLane(lane, outcome);

/** Closes the document at an externally observed timestamp. Idempotent. */
export const finishRendererDocument = (input: {
  outcome: RendererDocumentOutcome;
  endMs: number;
  endpoint: RendererEndpoint;
  destination: RendererDestination;
}): void => recorder().finishDocument(input);

/** Attaches (or detaches with `undefined`) the diagnostics consumer. */
export const setRendererDiagnosticsSink = (sink: RendererDiagnosticsSink | undefined): void =>
  recorder().setSink(sink);

/** The local snapshot, available even when analytics transport is disabled. */
export const readRendererDiagnostics = (): readonly RendererDiagnosticRow[] =>
  recorder().readRows();

/** The id correlating every row of this document. Distinct from `boot_id`. */
export const getRendererLoadId = (): string => recorder().getLoadId();

/** True once the document partition has been closed. */
export const isRendererDocumentFinished = (): boolean => recorder().isFinished();
